import { useQuery } from '@apollo/client';
import clsx from 'clsx';
import Image from 'next/image';
import { MicrobiomeTestCard } from '@snapi/report';
import { Baby, BabyWithTests, MicrobiomeTest, TestStatus } from '@snapi/types';
import { GetAllTestsBabyQuery } from '@snapi/query';
import { useMyAppContext } from '@snapi/ui-utils';
import { useMemo, useRef } from 'react';
import { BabyEditModal, BabyEditModalRef } from './baby-edit-modal';
import { BabySelector } from './baby-selector';
import { Button, Loading } from '@snapi/luxury';
import { MyTestCard } from './my-test-card';
import { createDataArray } from '../../utils/diversity-stats';
import { DiversityChart } from '@snapi/report';
import sunImage from './images/sun.png';
import lightbulbImage from './images/lightbulb.png';
import { Description } from '@snapi/luxury';
import { Subtitle } from '@snapi/luxury';
import { useToggle } from 'react-use';
import { motion } from 'framer-motion';
import { Variants } from 'framer-motion';

const dataArrayStatsByMonth = createDataArray(1.21716, 1.95676, 2.69635);

const CardVariants:Variants = {
  open: {
    height: 'auto',
    opacity: 1,
    pointerEvents: 'auto',
  },
  closed: {
    height: 0,
    opacity: 0,
    pointerEvents: 'none',
  },
};

type MicrobimeTestProjection = Pick<
  MicrobiomeTest,
  'id' | 'createdAt' | 'status'
>;

interface DasbhoardPageProps {
  babies: BabyWithTests[];
  onSave(baby: Baby): Promise<void>;
  onActivate(): void;
}

export function DasbhoardPage({
  babies,
  onSave,
  onActivate,
}: DasbhoardPageProps) {
  const [on, togle] = useToggle(false);
  const {
    state: { selected, invited },
    setSelected,
  } = useMyAppContext();
  const ref = useRef<BabyEditModalRef>(null);

  const currentTestObject = {
    testDiversity: null,
    ageTesting: 0,
    statsRange: [] as number[],
    status: '',
  };
  const previousTestObject = {
    testDiversity: null,
    ageTesting: 0,
    statsRange: [] as number[],
    status: '',
  };

  const { data, error, loading } = useQuery(GetAllTestsBabyQuery, {
    variables: { id: selected?.id },
  });

  if (
    data &&
    data.baby &&
    data.baby.tests &&
    data.baby.tests.length > 0 &&
    data.baby.tests[0].baby.tests[0].diversity !== null
  ) {
    const sortedData = [...data.baby.tests[0].baby.tests]
      .filter((item) => item.diversity !== null)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));

    currentTestObject.testDiversity = sortedData[0].diversity;
    currentTestObject.ageTesting = sortedData[0].ageInMonths;
    dataArrayStatsByMonth.find((item) => {
      if (item.month === sortedData[0].ageInMonths) {
        currentTestObject.statsRange.push(item.min, item.max);
        return true;
      }

      if (sortedData[0].ageInMonths > 57) {
        currentTestObject.statsRange.push(3.45498, 4.4449389);
        return true;
      }

      return false;
    });
  }
  if (
    data &&
    data.baby &&
    data.baby.tests &&
    data.baby.tests.length > 1 &&
    data.baby.tests[0].diversity !== null
  ) {
    const sortedData = [...data.baby.tests[0].baby.tests]
      .filter((item) => item.diversity !== null)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));

    if (sortedData.length > 1) {
      previousTestObject.testDiversity = sortedData[1].diversity;

      previousTestObject.ageTesting = sortedData[1].ageInMonths;
      dataArrayStatsByMonth.find((item) => {
        if (item.month === sortedData[1].ageInMonths) {
          previousTestObject.statsRange.push(item.min, item.max);
          return true;
        }

        if (sortedData[1].ageInMonths > 57) {
          previousTestObject.statsRange.push(3.45498, 4.4449389);
          return true;
        }

        return false;
      });
    }
  }

  function determineStatus(testObject: any) {
    const { testDiversity, statsRange } = testObject;

    if (testDiversity === null) {
      testObject.status = 'No data';
      return;
    }

    const lowerNumber = Math.min(...statsRange);
    const upperNumber = Math.max(...statsRange);

    if (testDiversity < lowerNumber) {
      testObject.status = 'Bad';
    }
    if (testDiversity >= lowerNumber && testDiversity <= upperNumber) {
      testObject.status = 'Good';
    }
    if (testDiversity > upperNumber) {
      testObject.status = 'Perfect';
    }
  }
  determineStatus(currentTestObject);
  determineStatus(previousTestObject);

  const phraseForTopGraph = () => {
    if (previousTestObject.testDiversity === null) {
      return currentTestObject.status === 'Bad'
        ? 'diversity needs improvement'
        : 'diversity is on track';
    }

    if (
      previousTestObject.status !== 'Bad' &&
      currentTestObject.status !== 'Bad'
    ) {
      return 'diversity is on track';
    }
    if (
      previousTestObject.status === 'Bad' &&
      currentTestObject.status !== 'Bad'
    ) {
      return 'diversity is on track';
    }
    if (
      previousTestObject.status !== 'Bad' &&
      currentTestObject.status === 'Bad'
    ) {
      return 'diversity needs improvement';
    }
    if (
      previousTestObject.status === 'Bad' &&
      currentTestObject.status === 'Bad'
    ) {
      return 'diversity needs improvement';
    } else return 'diversity needs improvement';
  };
  const phrase = phraseForTopGraph();

  const currentTests: MicrobimeTestProjection[] = useMemo(() => {
    if (!selected) {
      return [];
    }

    const tests = babies.find((b) => b.id === selected.id)?.tests ?? [];

    const firstReady = tests.find((test) => test.status === TestStatus.ready);
    const inProgress = tests.filter((test) => test.status !== TestStatus.ready);

    return firstReady ? [firstReady, ...inProgress] : inProgress;
  }, [selected, babies]);

  const previousTest: MicrobimeTestProjection[] = useMemo(() => {
    if (!selected) {
      return [];
    }
    const tests = babies.find((b) => b.id === selected.id)?.tests ?? [];

    return tests
      .filter((test) => test.status === TestStatus.ready)
      .filter((_, i) => i > 0);
  }, [selected, babies]);

  const handleSelected = (baby: Baby) => {
    const selected = babies.find(({ id }) => id === baby.id);
    if (selected) setSelected(selected);
  };

  const handleEdit = () => {
    if (selected) {
      ref.current?.edit(selected);
    }
  };

  const handleAdd = () => {
    ref.current?.add();
  };

  if (!selected) {
    return <Loading />;
  }
  
  

  return (
    <>
      <section>
        <div className="container">
          <div className="flex flex-col justify-center gap-2 md:gap-5 sm:flex-row">
            <div className="flex flex-1 max-w-[700px]">
              <BabySelector
                selected={selected}
                editable={!invited}
                babies={babies}
                onChange={handleSelected}
                onEdit={handleEdit}
              />
            </div>
            <div className="flex flex-col gap-1 justify-between sm:w-[280px]">
              <Button size="sm" onClick={onActivate} disabled={invited}>
                Add new test
              </Button>
              <Button size="sm" onClick={handleAdd}>
                Add baby
              </Button>
            </div>
          </div>
          <div className="md:flex items-start justify-start md:gap-5 max-w-[1000px] mx-auto">
            {data &&
            data.baby &&
            data.baby.tests &&
            data.baby.tests.length > 0 &&
            currentTests[0].status === 'ready' ? (
              <div className="sm:min-w-[404px] max-w-[700px] grow">
                <h5 className="mt-7 mb-2">{selected.name} Diversity Chart</h5>
                <div
                  className={clsx(
                    'flex items-center border-b-[1px] border-white p-4 mt-2 bg-blue-light rounded-t-[20px] gap-4',
                    !on && 'rounded-b-[20px] mb-2 border-none'
                  )}
                >
                  {phrase === 'diversity is on track' ? (
                    <div className="flex shrink-0 w-[48px] h-[48px] sm:w-[60px] sm:h-[60px] justify-center">
                      <Image src={sunImage} />{' '}
                    </div>
                  ) : (
                    <div className="flex shrink-0 w-[42px] h-[60px]  justify-center">
                      <Image src={lightbulbImage} />{' '}
                    </div>
                  )}
                  <div className="flex flex-col items-start">
                    <p className="font-semibold mb-[2px]">
                      {`${selected.name}'s`} {phrase}
                    </p>
                    <button
                      onClick={togle}
                      className={clsx(
                        'text-body text-opacity-80 disabled:text-gray-soft underline mt-2'
                      )}
                    >
                      <Subtitle>{on ? 'Show less' : 'Show more'}</Subtitle>
                    </button>
                  </div>
                </div>

                <motion.div
                  layout
                  animate={on ? 'open' : 'closed'}
                  transition={{ duration: 0.6 }}
                  variants={CardVariants}
                >
                  <div className="mb-7 p-4 bg-blue-light rounded-b-[20px] max-w-[700px]">
                    <h5 className="mt-1 mb-2">What does it mean?</h5>
                    <Description>
                      The Diversity tracking graph provides a visual
                      representation of your child's microbiome diversity over
                      time. The graph shows the range that children of the same
                      age normally have in blue and the point represents your
                      test. In general, diversity increases with age and a high
                      diversity is considered beneficial.
                    </Description>
                    <h5 className="mt-5 mb-2">How to interpret the results?</h5>
                    <Description>
                      To interpret the results, you should first review the
                      Diversity tracking graph. The graph shows the results of
                      all Snapi tests taken by your child as dots which allows
                      your to follow your progress over time.
                    </Description>
                    <div className="bg-white rounded-[8px] p-2 mt-3">
                      <Description>
                        If your child's result is above the blue range, it
                        indicates an{' '}
                        <span className="font-semibold">
                          excellent microbiome diversity.
                        </span>
                      </Description>
                    </div>
                    <div className="bg-white rounded-[8px] p-2 mt-1">
                      <Description>
                        If the result falls within the blue range, it is
                        considered{' '}
                        <span className="font-semibold">
                          normal microbiome diversity.
                        </span>
                      </Description>
                    </div>
                    <div className="bg-white rounded-[8px] p-2 mt-1">
                      <Description>
                        If the result falls below the blue range, it suggests{' '}
                        <span className="font-semibold">
                          low microbiome diversity.
                        </span>
                      </Description>
                    </div>

                    <h5 className="mt-5 mb-2">
                      Why is it important to keep testing?
                    </h5>
                    <Description>
                      Regular testing is important to monitor changes and
                      imbalances in your child's microbiome, which can affect
                      their health. Tracking the microbiome's diversity provides
                      insights for informed decisions on lifestyle and dietary
                      habits. Regular testing also helps evaluate the
                      effectiveness of interventions and make necessary
                      adjustments for optimal microbiome health.
                    </Description>
                  </div>
                </motion.div>
                <DiversityChart
                  stats={dataArrayStatsByMonth}
                  diversity={[...data.baby.tests[0].baby.tests]
                    .filter((item) => item.status === 'ready')
                    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))

                    .map(
                      (test: {
                        diversity: number;
                        ageInMonths: number;
                        id: string;
                        createdAt: string;
                      }) => {
                        return {
                          diversity: test.diversity,
                          ageInMonths: test.ageInMonths,
                          testId: test.id,
                          createdAt: test.createdAt,
                        };
                      }
                    )}
                />
              </div>
            ) : null}

            {currentTests.length > 0 && (
              <div
                className={clsx(
                  data &&
                    data.baby &&
                    data.baby.tests &&
                    data.baby.tests.length > 0 &&
                   currentTests[0].status !== 'ready'
                    ? 'flex flex-col gap-7 sm:gap-4 md:flex-row mt-10 md:mt-7 md:gap-5 w-full md:w-[700px]'
                    : 'flex flex-col gap-7 sm:gap-4 md:items-start mt-10 md:mt-7 md:w-[280px] md:gap-7'
                )}
              >
                <div className="flex flex-1 flex-col gap-2 max-w-[700px]">
                  <h5>Current test</h5>
                  <div className="flex flex-col gap-7">
                    {currentTests.map((test) => (
                      <MicrobiomeTestCard
                        key={test.id}
                        id={test.id}
                        status={test.status}
                        createdAt={test.createdAt}
                      />
                    ))}
                  </div>
                </div>
           { previousTest.length > 1 &&     <div className="flex flex-col gap-2 justify-start md:w-[280px]">
                  <h5>My tests</h5>
                  <div className="flex flex-col gap-2">
                    {previousTest.map((test) => (
                      <MyTestCard
                        key={test.id}
                        id={test.id}
                        createdAt={test.createdAt}
                      />
                    ))}
                  </div>
                </div>}
              </div>
            )}
          </div>
        </div>
      </section>
      <BabyEditModal ref={ref} onSave={onSave} />
    </>
  );
}
